import {
  Component,
  input,
  output,
  model,
  contentChild,
  TemplateRef,
  ChangeDetectionStrategy,
  ElementRef,
  viewChild,
  effect,
  OnDestroy,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { SkeletonComponent } from '../skeleton/skeleton';
import { AccessNoticeComponent } from '../access-notice/access-notice';
import { ErrorStateComponent } from '../error-state/error-state';
import { ToolbarSearchComponent } from '../toolbar-search/toolbar-search';
import { TranslocoModule } from '@jsverse/transloco';
@Component({
  selector: 'app-resource-card-grid',
  imports: [
    TranslocoModule,
    NgTemplateOutlet,
    MatIconModule,
    SkeletonComponent,
    AccessNoticeComponent,
    ErrorStateComponent,
    ToolbarSearchComponent,
  ],
  templateUrl: './resource-card-grid.html',
  styleUrl: './resource-card-grid.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceCardGridComponent implements OnDestroy {
  searchQuery = input('');
  searchPlaceholder = input('Search...');
  showSearch = input(true);

  loading = input(false);
  error = input<string | null>(null);
  /**
   * A capability denial's resolved sentence, from `resolveAccessDenial()`.
   *
   * Takes precedence over {@link error} and renders `<app-access-notice>`
   * instead of the hero error state — see that component for why a denial
   * must not be dressed as a failure. Null for every other kind of load
   * error, which keeps the hero + Refresh it has always had.
   */
  accessDenial = input<string | null>(null);
  isEmpty = input(false);
  skeletonCount = input(6);
  /**
   * Minimum width per card in the grid. The grid uses
   * `repeat(auto-fill, minmax(<minCardWidth>, 1fr))` so two columns
   * appear once the container reaches `2 × minCardWidth + gap`.
   *
   * Default `300px` is the modern dashboard convention (shadcn /
   * Linear / Vercel) and lets two cards fit at ~929 px viewport with
   * the sidebar open, vs ~1009 px for the previous 340 px default.
   * Card content (~268 px after padding) still has room for a
   * three-segment risk display + dual-date footer.
   *
   * Routes with denser card content can bump this back up by passing
   * `minCardWidth="340px"`.
   */
  minCardWidth = input('300px');

  hasMore = input(false);
  loadingMore = input(false);

  /** Optional custom skeleton template projected via `<ng-template listSkeleton>`. */
  listSkeletonTpl = contentChild<TemplateRef<unknown>>('listSkeleton');

  /** Optional custom grid skeleton template projected via `<ng-template gridSkeleton>`. */
  gridSkeletonTpl = contentChild<TemplateRef<unknown>>('gridSkeleton');

  viewMode = model<'grid' | 'list'>('grid');

  searchChange = output<string>();
  loadMore = output<void>();
  errorRetry = output<void>();

  /** Sentinel element at the bottom of the content area for infinite scroll. */
  private scrollSentinel = viewChild<ElementRef<HTMLElement>>('scrollSentinel');
  private observer: IntersectionObserver | null = null;

  constructor() {
    // React to sentinel element appearing/disappearing and hasMore/loadingMore changes.
    // Uses a generous rootMargin so content loads well before the user reaches the bottom.
    effect(() => {
      const sentinel = this.scrollSentinel();
      const hasMore = this.hasMore();
      const loadingMore = this.loadingMore();

      this.cleanupObserver();

      if (sentinel && hasMore && !loadingMore) {
        this.observer = new IntersectionObserver(
          entries => {
            if (entries[0]?.isIntersecting && this.hasMore() && !this.loadingMore()) {
              console.debug('📜 ResourceCardGrid: infinite scroll sentinel intersected');
              this.loadMore.emit();
            }
          },
          { rootMargin: '400px', threshold: 0 }
        );
        this.observer.observe(sentinel.nativeElement);
        console.debug('📜 ResourceCardGrid: observing infinite scroll sentinel');
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanupObserver();
  }

  skeletonItems(): number[] {
    return Array.from({ length: this.skeletonCount() }, (_, i) => i);
  }

  private cleanupObserver(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}
